/**
 * As perguntas que chegam antes da inscrição.
 *
 * Layout do template: à esquerda o título, o texto e o botão de contato, presos
 * enquanto a lista rola; à direita a sanfona. O `+` que vira `−` é o traço
 * vertical girando 90° — no template é uma animação Lottie de 40 KB baixada de
 * um CDN, aqui são dois `<span>` e um `transform`.
 *
 * Abre uma por vez. Com várias abertas ao mesmo tempo a lista cresce até a
 * resposta que a pessoa está lendo sair da tela sozinha, e ela perde o lugar
 * sem ter tocado em nada.
 *
 * As respostas apontam para onde a promessa está escrita por extenso. Resposta
 * que só existe aqui vira uma segunda fonte da verdade, e uma das duas
 * envelhece sem ninguém perceber.
 */

import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { useEntradaDaSecao } from './animacoes';

interface Pergunta {
  pergunta: string;
  resposta: string;
}

const PERGUNTAS: Pergunta[] = [
  {
    pergunta: 'Como inscrevo meu time?',
    resposta:
      'Crie sua conta, cadastre a equipe com o elenco e o ID do clube no EA SPORTS FC, e escolha a edição. Em campeonatos pagos a vaga é confirmada assim que o Pix cai — o código aparece na própria tela de inscrição.',
  },
  {
    pergunta: 'Como é feito o sorteio dos grupos?',
    resposta:
      'A partir de uma semente publicada junto com o resultado. Quem quiser conferir roda o mesmo sorteio com a mesma semente e chega aos mesmos grupos — ninguém, nem a organização, escolhe quem enfrenta quem.',
  },
  {
    pergunta: 'De onde vêm as estatísticas?',
    resposta:
      'Dos dados oficiais do EA SPORTS FC. Um coletor lê as partidas do clube e casa cada uma com o confronto correspondente da edição, e é isso que alimenta artilharia, assistências e o histórico do time.',
  },
  {
    pergunta: 'Preciso do ID do clube na EA?',
    resposta:
      'Sim, e ele é conferido no cadastro. Sem o ID não há como puxar as partidas do seu time, e o placar dependeria de print — que é justamente o que a conferência automática existe para evitar.',
  },
  {
    pergunta: 'O que acontece se o adversário não aparecer?',
    resposta:
      'O capitão abre um ticket com a evidência e a organização decide pelo W.O. dentro do prazo do regulamento. A decisão e o motivo ficam registrados na página da partida.',
  },
  {
    pergunta: 'Quando a premiação é paga?',
    resposta:
      'Via Pix, depois do encerramento oficial da edição. O valor de cada colocação fica visível desde a inscrição e não muda depois que as inscrições abrem.',
  },
];

export function SecaoPerguntas() {
  const raiz = useRef<HTMLElement>(null);
  const [aberta, setAberta] = useState<number | null>(0);

  useEntradaDaSecao(raiz, '[data-entra]');

  return (
    <section className="faqs-section" ref={raiz}>
      <div className="container">
        <div className="wrapper faqs-wrapper">
          <div className="faqs-left-content" data-entra>
            <div className="faqs-top">
              {/* Sem quebra forçada: na coluna estreita da grade o título já
                  quebra sozinho, e o <br /> deixava uma linha órfã. */}
              <h2 className="faqs-title">Dúvidas? Aqui vão as respostas.</h2>
              <p className="faqs-para">
                Da inscrição ao dia do jogo — o que os capitães perguntam antes de trazer o
                elenco.
              </p>
            </div>

            <div className="faqs-bottomn">
              <div className="questions-text">Ficou alguma pergunta de fora?</div>
              <Link to="/suporte" className="button-3">
                <div className="button-3-text">Falar com a organização</div>
                <div className="button-3-bg" aria-hidden />
              </Link>
            </div>
          </div>

          <div className="faqs-lists-wrap">
            {PERGUNTAS.map((item, indice) => (
              <ItemDaSanfona
                key={item.pergunta}
                item={item}
                aberta={aberta === indice}
                aoAlternar={() => setAberta(aberta === indice ? null : indice)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ItemDaSanfona({
  item,
  aberta,
  aoAlternar,
}: {
  item: Pergunta;
  aberta: boolean;
  aoAlternar: () => void;
}) {
  return (
    <div className={`faq-list-wrap${aberta ? ' aberta' : ''}`} data-entra>
      <h3>
        <button
          type="button"
          className="faq-question"
          aria-expanded={aberta}
          onClick={aoAlternar}
        >
          <div className="faq-ques">{item.pergunta}</div>
          <span className="faq-icone" aria-hidden>
            <span className="faq-traco" />
            <span className="faq-traco faq-mais" />
          </span>
        </button>
      </h3>

      <div className="faq-answer">
        <div>
          <p className="faq-para">{item.resposta}</p>
        </div>
      </div>
    </div>
  );
}
